import React from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    DialogContent,
    MenuItem,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import DraggableDialog from '../../../ui/draggableDialog';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../../utils/environment';
import { ValidDataService } from '../../../../service/validDataService';
import { initialValuesManufacturerForm } from './validManufacturerInputForm';
import SectionTitle from '../../../ui/sectionTitle';
import ManufacturerBrandList from './manufacturerBrandList';
import SafeDialogContent from 'components/ui/SafeDialogContent';

export default function EditValidManufacturerForm({ open, onClose, onRecordEdit, recordToEdit, isCopy = false }) {

    const handleSubmit = async (values, { resetForm }) => {
        try {
            let response;

            if (isCopy) {
                // Remove the id field to avoid duplicate key errors
                const { id, ...newValues } = values;
                response = await ValidDataService.addValidManufacturer(newValues);
            } else {
                response = await ValidDataService.updateValidManufacturer(values);
            }
            
            const { code, data, message } = response;
            if (code === 0) {
                toast.success(message);
                onClose();
                onRecordEdit(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            toast.error('Failed to update manufacturer');
        }
    };

    return (
        <DraggableDialog title={isCopy ? "Copy Manufacturer" : "Edit Manufacturer"} onClose={onClose} open={open} width={'md'}>
            <SafeDialogContent>
                <Formik
                    initialValues={{ ...initialValuesManufacturerForm, ...recordToEdit }}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                {/* Basic Info */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Basic Information" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="name"
                                        label="Manufacturer Name"
                                        required
                                        fullWidth
                                        size="small"
                                    />
                                    <ErrorMessage name="name">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="type"
                                        label="Manufacturer Type"
                                        select
                                        required
                                        fullWidth
                                        size="small"
                                    >
                                        <MenuItem value="frame">Frame</MenuItem>
                                        <MenuItem value="lens">Lens</MenuItem>
                                        <MenuItem value="contact_lens">Contact Lens</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="contactPerson"
                                        label="Contact Person"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="phone"
                                        label="Phone Number"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>

                                {/* Contact Details */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Contact Details" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="email"
                                        label="Email"
                                        type="email"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="website"
                                        label="Website"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>

                                {/* Address */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Address" />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="address"
                                        label="Street Address"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name="city"
                                        label="City"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name="state"
                                        label="State/Province"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name="zip"
                                        label="Postal/Zip Code"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="country"
                                        label="Country"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>

                                {/* Notes */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Additional Info" />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={4}
                                        fullWidth
                                    />
                                </Grid>

                                {/* Brands - only show for existing manufacturers (not when copying) */}
                                {!isCopy && recordToEdit?.id && (
                                    <Grid item xs={12}>
                                        <SectionTitle title="Brands" />
                                        <ManufacturerBrandList manufacturerId={recordToEdit.id} />
                                    </Grid>
                                )}

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        {isCopy ? 'Create Copy' : 'Update'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </SafeDialogContent>
        </DraggableDialog>
    );
};
